import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { Logo, LOGO_URL } from "../components/Logo";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { Search, MapPin, Stethoscope, MessageCircle, CalendarPlus, Loader2, ArrowLeft, Star, BellRing, BadgeCheck } from "lucide-react";

const TEAL = "#0D9488";

function waLink(phone, name, docName) {
  const digits = (phone || "").replace(/\D/g, "");
  const full = digits.length === 10 ? `52${digits}` : digits;
  const msg = `Hola Dr(a). ${docName || ""}, me gustaría agendar una cita. Los encontré en el directorio de ClinPed.`;
  return `https://wa.me/${full}?text=${encodeURIComponent(msg)}`;
}

function RequestDialog({ doctor, onClose }) {
  const [form, setForm] = useState({ patient_name: "", phone: "", reason: "", preferred_date: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!form.patient_name.trim() || !form.phone.trim()) { toast.error("Escribe tu nombre y teléfono"); return; }
    setSending(true);
    try {
      await api.post(`/public/doctors/${doctor.doctor_id}/request`, form);
      setDone(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo enviar la solicitud"); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={!!doctor} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl max-w-md" data-testid="request-dialog">
        <DialogHeader><DialogTitle className="font-outfit text-brand-ink">Solicitar cita con {doctor?.name}</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Formulario para solicitar una cita con el médico seleccionado.</DialogDescription>
        {done ? (
          <div className="text-center py-4" data-testid="request-success">
            <div className="w-14 h-14 rounded-2xl grid place-items-center mx-auto mb-3 text-white" style={{ backgroundColor: TEAL }}><CalendarPlus size={26} /></div>
            <p className="font-semibold text-brand-ink">¡Solicitud enviada!</p>
            <p className="text-sm text-muted-foreground mt-1">El médico recibirá tu solicitud en la app. También puedes contactarlo por WhatsApp para agilizar.</p>
            <a href={waLink(doctor.phone, doctor.name, doctor.name)} target="_blank" rel="noreferrer"
              className="mt-4 inline-flex items-center justify-center gap-2 w-full bg-[#25D366] text-white font-semibold py-3 rounded-2xl">
              <MessageCircle size={18} /> Escribir por WhatsApp
            </a>
          </div>
        ) : (
          <div className="space-y-3">
            <input data-testid="req-name" value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })}
              placeholder="Tu nombre (o del paciente)" className="w-full px-3 py-2.5 rounded-xl border border-stone-200 outline-none focus:border-teal-500 text-sm" />
            <input data-testid="req-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="Tu teléfono / WhatsApp" className="w-full px-3 py-2.5 rounded-xl border border-stone-200 outline-none focus:border-teal-500 text-sm" />
            <input data-testid="req-date" value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })}
              placeholder="Fecha/horario preferido (opcional)" className="w-full px-3 py-2.5 rounded-xl border border-stone-200 outline-none focus:border-teal-500 text-sm" />
            <textarea data-testid="req-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} rows={2}
              placeholder="Motivo de la consulta (opcional)" className="w-full px-3 py-2.5 rounded-xl border border-stone-200 outline-none focus:border-teal-500 text-sm" />
            <button data-testid="req-submit" onClick={submit} disabled={sending}
              className="w-full flex items-center justify-center gap-2 text-white font-outfit font-semibold py-3 rounded-2xl transition-all disabled:opacity-60" style={{ backgroundColor: TEAL }}>
              {sending ? <Loader2 size={17} className="animate-spin" /> : <CalendarPlus size={17} />} Enviar solicitud
            </button>
            <p className="text-center text-xs text-muted-foreground">o contáctalo directo por WhatsApp desde su tarjeta.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function NotifyDialog({ doctor, onClose }) {
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const notify = async () => {
    setSending(true);
    try {
      await api.post(`/public/doctors/${doctor.doctor_id}/notify`);
      setDone(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo enviar el aviso"); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={!!doctor} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl max-w-md" data-testid="notify-dialog">
        <DialogHeader><DialogTitle className="font-outfit text-brand-ink">Contactar a {doctor?.name}</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Aviso al médico de que un paciente lo busca.</DialogDescription>
        {done ? (
          <div className="text-center py-4" data-testid="notify-success">
            <div className="w-14 h-14 rounded-2xl grid place-items-center mx-auto mb-3 text-white bg-amber-500"><BellRing size={26} /></div>
            <p className="font-semibold text-brand-ink">¡Le avisamos!</p>
            <p className="text-sm text-muted-foreground mt-1">Notificamos a {doctor?.name} que un paciente lo busca en ClinPed. Cuando reactive su cuenta podrás contactarlo directamente.</p>
          </div>
        ) : (
          <div className="text-center py-2">
            <div className="w-14 h-14 rounded-2xl grid place-items-center mx-auto mb-3 bg-amber-50 text-amber-600"><BellRing size={26} /></div>
            <p className="text-sm text-brand-ink/80 leading-relaxed">
              <b>{doctor?.name}</b> no está disponible para contacto directo por el momento.
              Podemos <b>avisarle que un paciente lo busca</b> en ClinPed (no compartimos tus datos).
            </p>
            <button data-testid="notify-submit" onClick={notify} disabled={sending}
              className="mt-5 w-full flex items-center justify-center gap-2 text-white font-outfit font-semibold py-3 rounded-2xl transition-all disabled:opacity-60 bg-amber-500 hover:bg-amber-600">
              {sending ? <Loader2 size={17} className="animate-spin" /> : <BellRing size={17} />} Avisarle que lo busco
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DoctorCard({ d, onRequest, onNotify }) {
  const inactive = d.subscription_active === false;
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} whileHover={{ y: -4 }}
      data-testid={`doctor-card-${d.doctor_id}`} className="bg-white rounded-3xl border border-stone-100 shadow-[0_8px_30px_rgb(61,64,91,0.06)] overflow-hidden">
      <div className="h-20 bg-gradient-to-r from-teal-500 to-emerald-500 relative">
        <div className="absolute top-2.5 right-3 flex items-center gap-1.5 bg-white/90 backdrop-blur rounded-full pl-1 pr-2.5 py-1 shadow-sm" data-testid={`card-brand-${d.doctor_id}`}>
          <img src={LOGO_URL} alt="ClinPed" className="h-5 w-5 object-contain" />
          <span className="font-outfit font-extrabold text-xs text-brand-ink tracking-tight leading-none">Clin<span className="text-brand-terracotta">Ped</span></span>
        </div>
      </div>
      <div className="px-6 pb-6 -mt-10 relative z-10">
        <div className="w-20 h-20 rounded-2xl overflow-hidden ring-4 ring-white bg-stone-100 shadow-md">
          {d.photo ? <img src={d.photo} alt={d.name} className="w-full h-full object-cover object-top" /> : <div className="w-full h-full grid place-items-center text-teal-600"><Stethoscope size={28} /></div>}
        </div>
        <h3 className="font-outfit font-extrabold text-lg text-brand-ink mt-3 flex items-center gap-1.5">
          {d.name}
          {d.verified && (
            <span data-testid={`doctor-verified-${d.doctor_id}`} title="Profesional verificado por ClinPed · Cédula consultada en el Registro Nacional de Profesionistas"
              className="inline-flex items-center gap-1 text-[10px] font-bold text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full shrink-0">
              <BadgeCheck size={12} /> Verificado
            </span>
          )}
        </h3>
        <p className="text-sm font-semibold text-teal-600">{d.specialty}</p>
        {d.clinic_name && <p className="text-xs text-muted-foreground mt-0.5">{d.clinic_name}</p>}
        {d.catalog_bio && <p className="text-sm text-brand-ink/70 mt-2 line-clamp-3">{d.catalog_bio}</p>}
        {d.service_areas?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {d.service_areas.map((a) => (
              <span key={a} className="flex items-center gap-1 text-[11px] font-semibold bg-teal-50 text-teal-700 rounded-full px-2.5 py-0.5"><MapPin size={11} /> {a}</span>
            ))}
          </div>
        )}
        <div className="flex gap-2 mt-5">
          {inactive ? (
            <>
              <button data-testid={`doctor-wa-${d.doctor_id}`} onClick={() => onNotify(d)}
                className="flex-1 flex items-center justify-center gap-2 bg-[#25D366] text-white font-semibold py-2.5 rounded-xl text-sm hover:brightness-95 transition-all">
                <MessageCircle size={16} /> WhatsApp
              </button>
              <button data-testid={`doctor-request-${d.doctor_id}`} onClick={() => onNotify(d)}
                className="flex-1 flex items-center justify-center gap-2 text-white font-semibold py-2.5 rounded-xl text-sm transition-all hover:brightness-95" style={{ backgroundColor: TEAL }}>
                <CalendarPlus size={16} /> Cita
              </button>
            </>
          ) : (
            <>
              <a data-testid={`doctor-wa-${d.doctor_id}`} href={waLink(d.phone, d.name, d.name)} target="_blank" rel="noreferrer"
                className="flex-1 flex items-center justify-center gap-2 bg-[#25D366] text-white font-semibold py-2.5 rounded-xl text-sm hover:brightness-95 transition-all">
                <MessageCircle size={16} /> WhatsApp
              </a>
              <button data-testid={`doctor-request-${d.doctor_id}`} onClick={() => onRequest(d)}
                className="flex-1 flex items-center justify-center gap-2 text-white font-semibold py-2.5 rounded-xl text-sm transition-all hover:brightness-95" style={{ backgroundColor: TEAL }}>
                <CalendarPlus size={16} /> Cita
              </button>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default function DoctorDirectory() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const focusDoc = sp.get("doc");
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [filters, setFilters] = useState({ cities: [], specialties: [] });
  const [requestDoc, setRequestDoc] = useState(null);
  const [notifyDoc, setNotifyDoc] = useState(null);

  useEffect(() => {
    if (focusDoc) return; // en modo perfil no cargamos filtros
    api.get("/public/doctors/filters").then((r) => setFilters(r.data)).catch(() => {});
  }, [focusDoc]);

  useEffect(() => {
    if (focusDoc) {
      setLoading(true); setNotFound(false);
      api.get(`/public/doctors/${focusDoc}`)
        .then((r) => setDoctors([r.data]))
        .catch(() => { setDoctors([]); setNotFound(true); })
        .finally(() => setLoading(false));
      return;
    }
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (city) params.city = city;
    if (specialty) params.specialty = specialty;
    const t = setTimeout(() => {
      api.get("/public/doctors", { params }).then((r) => setDoctors(r.data)).catch(() => setDoctors([])).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, city, specialty, focusDoc]);

  return (
    <div className="min-h-screen bg-background" data-testid="directory-page">
      <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b border-stone-100">
        <div className="max-w-6xl mx-auto px-5 py-3 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2"><Logo size={34} /></button>
          <button data-testid="dir-login-btn" onClick={() => navigate("/login")} className="text-sm font-semibold text-teal-600 flex items-center gap-1"><ArrowLeft size={15} /> Soy médico</button>
        </div>
      </header>

      <section className="px-5 py-12 md:py-16 text-center bg-gradient-to-b from-teal-50/60 to-transparent">
        <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-teal-700 bg-teal-100 px-3 py-1 rounded-full"><Star size={12} /> Directorio ClinPed</span>
        <h1 className="font-outfit font-extrabold text-3xl md:text-5xl text-brand-ink tracking-tight mt-4">Contacta a tu médico</h1>
        <p className="text-brand-ink/60 mt-3 max-w-xl mx-auto">Encuentra especialistas cerca de ti y solicita tu cita en segundos, por WhatsApp o directo en la app.</p>

        {/* Buscador + filtros (ocultos en modo perfil) */}
        {!focusDoc && (
        <div className="max-w-3xl mx-auto mt-8 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input data-testid="dir-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nombre, especialidad o ciudad…"
              className="w-full pl-10 pr-3 py-3 rounded-2xl border border-stone-200 outline-none focus:border-teal-500 bg-white" />
          </div>
          <select data-testid="dir-city" value={city} onChange={(e) => setCity(e.target.value)} className="px-4 py-3 rounded-2xl border border-stone-200 outline-none focus:border-teal-500 bg-white text-sm">
            <option value="">Todas las ciudades</option>
            {filters.cities.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select data-testid="dir-specialty" value={specialty} onChange={(e) => setSpecialty(e.target.value)} className="px-4 py-3 rounded-2xl border border-stone-200 outline-none focus:border-teal-500 bg-white text-sm">
            <option value="">Todas las especialidades</option>
            {filters.specialties.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        )}
        {focusDoc && (
          <button data-testid="dir-see-all" onClick={() => navigate("/directorio")} className="mt-6 text-sm font-semibold text-teal-600 hover:underline">Ver todos los médicos</button>
        )}
      </section>

      <section className="px-5 pb-20 max-w-6xl mx-auto">
        {loading ? (
          <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-teal-500" size={30} /></div>
        ) : notFound ? (
          <div className="text-center py-20" data-testid="dir-unavailable">
            <div className="w-14 h-14 rounded-2xl bg-stone-100 text-stone-400 grid place-items-center mx-auto mb-4"><Stethoscope size={26} /></div>
            <p className="font-semibold text-brand-ink">Este perfil no está disponible</p>
            <p className="text-sm text-muted-foreground mt-1">El médico no tiene una suscripción activa en este momento.</p>
          </div>
        ) : doctors.length === 0 ? (
          <div className="text-center py-20" data-testid="dir-empty">
            <div className="w-14 h-14 rounded-2xl bg-teal-50 text-teal-600 grid place-items-center mx-auto mb-4"><Stethoscope size={26} /></div>
            <p className="font-semibold text-brand-ink">Aún no hay médicos que coincidan</p>
            <p className="text-sm text-muted-foreground mt-1">Prueba con otra ciudad o especialidad.</p>
          </div>
        ) : (
          <div className={focusDoc ? "max-w-sm mx-auto" : "grid sm:grid-cols-2 lg:grid-cols-3 gap-6"} data-testid="doctor-grid">
            {doctors.map((d) => <DoctorCard key={d.doctor_id} d={d} onRequest={setRequestDoc} onNotify={setNotifyDoc} />)}
          </div>
        )}
      </section>

      <footer className="border-t border-stone-100 bg-stone-50/60" data-testid="verification-legend">
        <div className="max-w-4xl mx-auto px-5 py-6 text-center">
          <p className="text-[11px] leading-relaxed text-stone-500 flex items-start sm:items-center justify-center gap-1.5 flex-wrap">
            <BadgeCheck size={13} className="text-teal-500 shrink-0" />
            Los médicos con el sello <b className="text-stone-600">"Profesional verificado por ClinPed"</b> completaron su registro y ClinPed verificó la coincidencia de sus datos con el Registro Nacional de Profesionistas.
          </p>
        </div>
      </footer>

      {requestDoc && <RequestDialog doctor={requestDoc} onClose={() => setRequestDoc(null)} />}
      {notifyDoc && <NotifyDialog doctor={notifyDoc} onClose={() => setNotifyDoc(null)} />}
    </div>
  );
}
